import React from 'react';
import { Combobox } from '@base-ui/react/combobox';
import styles from './CampoSeletor.module.css';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiClose } from '@mdi/js';

export type CampoSeletorLivreOption = {
    id: string;
    label: string;
};

type CampoSeletorLivreProps = {
    value?: string | null;
    onChange?: (value: string | null) => void;
    label?: string;
    placeholder?: string;
    options: CampoSeletorLivreOption[];
    nullable?: boolean;
    disabled?: boolean;
    error?: string;
    hint?: string;
};

/**
 * Campo que combina seletor com lista de sugestões e entrada livre de texto.
 * O usuário pode escolher uma opção predefinida OU digitar qualquer valor.
 * O valor é sempre enviado em caixa baixa.
 */
export default function CampoSeletorLivre({
    value,
    onChange,
    label,
    placeholder,
    options,
    nullable,
    disabled,
    error,
    hint,
}: Readonly<CampoSeletorLivreProps>) {
    const handleInputChange = (text: string) => {
        const lower = text.toLowerCase();
        onChange?.(lower || null);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange?.(null);
    };

    return (
        <div style={{ width: '100%' }}>
            {label && <label className={styles.label}>{label}</label>}
            <Combobox.Root
                inputValue={value ?? ''}
                onInputValueChange={handleInputChange}
                disabled={disabled}
            >
                <div
                    className={[
                        styles.container,
                        error ? styles.error : null,
                        disabled ? styles.disabled : null,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    <Combobox.Input
                        placeholder={placeholder}
                        style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            color: 'inherit',
                            font: 'inherit',
                            minWidth: 0,
                            padding: 0,
                        }}
                    />
                    {nullable && value && (
                        <span
                            onClick={handleClear}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                            }}
                        >
                            <Icon path={mdiClose} size="20px" />
                        </span>
                    )}
                    <Combobox.Icon className={styles.arrow}>
                        <Icon path={mdiChevronDown} size={1} />
                    </Combobox.Icon>
                </div>
                <Combobox.Portal>
                    <Combobox.Positioner
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        className={styles.positioner}
                    >
                        <Combobox.Popup className={styles.options}>
                            <Combobox.List>
                                {options.map((option) => (
                                    <Combobox.Item
                                        key={option.id}
                                        value={option.id}
                                        className={styles.option}
                                    >
                                        {option.label}
                                    </Combobox.Item>
                                ))}
                            </Combobox.List>
                            <Combobox.Empty
                                style={{ padding: '8px', fontSize: '0.875rem', opacity: 0.6 }}
                            >
                                Nenhuma sugestão — digite e aplique
                            </Combobox.Empty>
                        </Combobox.Popup>
                    </Combobox.Positioner>
                </Combobox.Portal>
            </Combobox.Root>
            {hint && !error && <div className={styles.hint}>{hint}</div>}
            {error && <div className={styles.error}>{error}</div>}
        </div>
    );
}
